"use client";

import { Cancel } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Button, Typography } from "@mui/material";
import { useTheme } from "../theme/ThemeContext";
import { PlanCode } from "@/types/subscription";
import { isPlanDowngrade } from "./PlanCommon";
import { useTranslations } from "next-intl";
import { useRef, useEffect, useState } from "react";

/**
 * 定义计划列的数据结构
 * 每一列代表一个订阅计划或功能对比类别
 */
interface PlanColumn {
  planCode: undefined | PlanCode; // 唯一标识符，可以是字符串或PlanCode
  title: string; // 列标题（如"Explorer", "Visionary"等）
  subtitle?: string; // 副标题（如"(Monthly)", "(Yearly)"等）
  hasBackground?: boolean; // 是否显示背景色（用于视觉区分）
  buttonType?: "contained" | "none"; // 按钮类型
  features: (boolean | string)[]; // 功能特性数组，对应每一行的值
}

/**
 * 组件属性接口
 */
interface PlanComparisonTableProps {
  currentPlanCode?: PlanCode; // 当前用户的订阅计划ID，如果没有订阅则为undefined
  onPlanClick?: (planCode: PlanCode) => void; // 计划点击回调函数，传递PlanCode
}

/**
 * 计划对比表组件
 *
 * 主要功能：
 * 1. 展示不同订阅计划的功能对比
 * 2. 实现水平滚动，固定第一列（Benefits）
 * 3. 支持主题切换（深色/浅色模式）
 * 4. 动态背景色和悬停效果
 * 5. 响应式设计，适配不同屏幕尺寸
 * 6. 支持计划降级限制和点击回调
 *
 * 设计特点：
 * - 采用列式数据组织方式，便于维护
 * - 固定第一列，其余列可水平滚动
 * - 交替列背景色，提升可读性
 * - 支持布尔值和字符串类型的功能描述
 * - 防止计划降级，禁用低级别计划按钮
 */
export default function PlanComparisonTable({ currentPlanCode, onPlanClick }: PlanComparisonTableProps) {
  const theme = useTheme();
  const t = useTranslations("Membership.comparison");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  
  // 鼠标拖拽滑动状态
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollStart, setScrollStart] = useState(0);

  // 检测滚动状态
  const checkScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    
    // 左侧阴影：当可以向左滚动时显示
    setShowLeftShadow(scrollLeft > 0);
    
    // 右侧阴影：当可以向右滚动时显示
    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  // 鼠标拖拽开始
  const handleMouseDown = (e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    setIsDragging(true);
    setStartX(e.pageX - container.offsetLeft);
    setScrollStart(container.scrollLeft);
    
    // 防止文本选择
    e.preventDefault();
  };

  // 鼠标拖拽移动
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const container = scrollContainerRef.current;
    if (!container) return;

    e.preventDefault();
    
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 2; // 滚动速度倍数，可以调整
    container.scrollLeft = scrollStart - walk;
  };

  // 鼠标拖拽结束
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 鼠标离开容器
  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // 组件挂载时和窗口大小改变时检查滚动状态
  useEffect(() => {
    checkScrollPosition();
    
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      return () => container.removeEventListener('scroll', checkScrollPosition);
    }
  }, []);

  // 窗口大小改变时重新检查
  useEffect(() => {
    const handleResize = () => checkScrollPosition();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 全局鼠标事件监听器，处理拖拽过程中鼠标离开容器的情况
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      const container = scrollContainerRef.current;
      if (!container) return;

      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 2; // 滚动速度倍数
      container.scrollLeft = scrollStart - walk;
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, startX, scrollStart]);

  /**
   * 处理计划点击事件
   * @param planCode - 被点击的计划代码，只处理 PlanCode 类型
   */
  const handlePlanClick = (planCode: PlanCode | undefined) => {
    // 只处理有效的 PlanCode
    if (!planCode) return;

    // 检查是否为降级，如果是则阻止操作
    if (isPlanDowngrade(currentPlanCode, planCode)) return;

    // 调用回调函数
    onPlanClick?.(planCode);
  };

  // 获取翻译数据
  const features: string[] = t.raw("features");
  const planTitles = {
    normal: t("planTitles.normal"),
    freePlan: t("planTitles.freePlan"),
    explorer: t("planTitles.explorer"),
    explorerSubtitle: t("planTitles.explorerSubtitle"),
    visionary: t("planTitles.visionary"),
    visionarySubtitle: t("planTitles.visionarySubtitle"),
    prime: t("planTitles.prime"),
    primeSubtitle: t("planTitles.primeSubtitle"),
  };
  const featureValues = {
    basicOnly: t("featureValues.basicOnly"),
    limited: t("featureValues.limited"),
    freeForever: t("featureValues.freeForever"),
    nAFreeForever: t("featureValues.nAFreeForever"),
    monthlyRenewal: t("featureValues.monthlyRenewal"),
    oneYearAccess: t("featureValues.oneYearAccess"),
    neverExpires: t("featureValues.neverExpires"),
  };

  // 按列组织的计划数据 - 每列代表一个订阅计划
  // 采用列式组织便于添加新计划和维护现有数据
  const planColumns: PlanColumn[] = [
    {
      // Benefits列 - 固定在左侧，不参与滚动
      planCode: undefined, // Benefits列没有对应的PlanCode
      title: t("benefits"),
      features: features,
    },
    {
      // 其他加密平台
      planCode: undefined,
      title: planTitles.normal,
      hasBackground: true, // 显示背景色以区分
      buttonType: "none", // 不显示升级按钮
      features: [
        false, // RightPulse Investing Signals
        featureValues.basicOnly, // Chart Access
        false, // Mining Hub
        featureValues.basicOnly, // Daily Indicator Signal
        false, // High-Beta Portfolio Overview
        featureValues.limited, // Quantitative Research
        false, // Investing Citadel Access
        false, // 1v1 Dedicated Service Group
        false, // OG Pass Badge
        false, // Exclusive Promotion Spot
        false, // Identity Badge
        false, // Permanent 20% Discount
        false, // Group
        featureValues.nAFreeForever, // Membership Expiry
      ],
    },
    {
      // 免费计划
      planCode: undefined,
      title: planTitles.freePlan,
      hasBackground: false, // 不显示背景色
      buttonType: "none", // 免费计划不需要升级按钮
      features: [
        false, // RightPulse Investing Signals
        featureValues.basicOnly, // Chart Access
        true, // Mining Hub
        false, // Daily Indicator Signal
        false, // High-Beta Portfolio Overview
        featureValues.limited, // Quantitative Research
        true, // Investing Citadel Access
        false, // 1v1 Dedicated Service Group
        false, // OG Pass Badge
        false, // Exclusive Promotion Spot
        false, // Identity Badge
        false, // Permanent 20% Discount
        featureValues.limited, // Group
        featureValues.freeForever, // Membership Expiry
      ],
    },
    {
      // Explorer计划 - 月付订阅
      planCode: PlanCode.EXPLORER,
      title: planTitles.explorer,
      subtitle: planTitles.explorerSubtitle,
      hasBackground: true, // 显示背景色
      buttonType: "contained", // 边框按钮样式
      features: [
        true, // RightPulse Investing Signals
        true, // Chart Access
        true, // Mining Hub
        true, // Daily Indicator Signal
        false, // High-Beta Portfolio Overview
        true, // Quantitative Research
        true, // Investing Citadel Access
        false, // 1v1 Dedicated Service Group
        false, // OG Pass Badge
        false, // Exclusive Promotion Spot
        false, // Identity Badge
        false, // Permanent 20% Discount
        featureValues.limited, // Group
        featureValues.monthlyRenewal, // Membership Expiry
      ],
    },
    {
      // Visionary计划 - 年付订阅，推荐计划
      planCode: PlanCode.VISIONARY,
      title: planTitles.visionary,
      subtitle: planTitles.visionarySubtitle,
      hasBackground: false, // 不显示背景色以突出差异
      buttonType: "contained", // 实心按钮样式
      features: [
        true, // RightPulse Investing Signals
        true, // Chart Access
        true, // Mining Hub
        true, // Daily Indicator Signal
        false, // High-Beta Portfolio Overview
        true, // Quantitative Research
        true, // Investing Citadel Access
        false, // 1v1 Dedicated Service Group
        false, // OG Pass Badge
        false, // Exclusive Promotion Spot
        false, // Identity Badge
        false, // Permanent 20% Discount
        true, // Group
        featureValues.oneYearAccess, // Membership Expiry
      ],
    },
    {
      // Prime Pass计划 - 终身订阅，最高级别
      planCode: PlanCode.PRIME,
      title: planTitles.prime,
      subtitle: planTitles.primeSubtitle,
      hasBackground: true, // 显示背景色
      buttonType: "contained", // 边框按钮
      features: [
        true, // RightPulse Investing Signals
        true, // Chart Access
        true, // Mining Hub
        true, // Daily Indicator Signal
        true, // High-Beta Portfolio Overview
        true, // Quantitative Research
        true, // Investing Citadel Access
        true, // 1v1 Dedicated Service Group
        true, // OG Pass Badge
        true, // Exclusive Promotion Spot
        true, // Identity Badge
        true, // Permanent 20% Discount
        true, // Group
        featureValues.neverExpires, // Membership Expiry
      ],
    },
  ];

  /**
   * 渲染功能特性值的函数
   * @param value - 布尔值或字符串类型的功能值
   * @returns 对应的React元素（图标或文本）
   *
   * 渲染规则：
   * - true: 显示绿色对勾图标
   * - false: 显示红色取消图标
   * - 字符串: 显示为灰色文本
   */
  const renderFeatureValue = (value: boolean | string) => {
    if (typeof value === "string") {
      return (
        <Typography variant="body2" sx={{ fontSize: "var(--font-size-xs)", fontWeight: 600 }}>
          {value}
        </Typography>
      );
    }
    return value ? <CheckCircleIcon color="success" fontSize="small" /> : <Cancel color="error" fontSize="small" />;
  };

  return (
    <Box sx={{ mt: "9.5rem", mb: "10rem" }}>
      {/* 标题区域 */}
      <Typography
        variant="h4"
        sx={{
          textAlign: "center",
          fontSize: "var(--font-size-3xl)",
          fontWeight: 600,
        }}>
        {t.rich("title", { br: () => <br /> })}
      </Typography>

      {/* 副标题描述 */}
      <Typography
        variant="body1"
        sx={{
          textAlign: "center",
          color: "text.secondary",
          mt: "1.81rem",
          mb: "4.5rem",
          maxWidth: "49.5rem",
          mx: "auto",
        }}>
        {t.rich("description", { br: () => <br /> })}
      </Typography>

      {/* 主要对比表容器 */}
      <Box
        sx={{
          display: "flex",
          mr: { xs: "-1rem", sm: "0" },
        }}>
        {/* 固定第一列 - Benefits列，始终可见不参与滚动 */}
        <Box
          sx={{
            width: {xs: "10rem", sm: "17.5rem"}, // 固定宽度
            flexShrink: 0, // 不允许收缩
            bgcolor: "background.default", // 使用主题背景色
            pt: "1rem",
            pr: { xs: "0.25rem", sm: "0.5rem" }, // 响应式内边距
          }}>
          {/* 固定列表头 */}
          <Box
            sx={{
              mb: "1rem",
              height: "3rem",
              display: "flex",
              alignItems: "center",
            }}>
            <Typography variant="h6" sx={{ fontSize: "var(--font-size-xs)", fontWeight: "600", color: "text.primary" }}>
              {t("benefits")}
            </Typography>
          </Box>

          {/* 固定列内容行 - 功能特性名称列表 */}
          {planColumns[0].features.map((feature, rowIndex) => (
            <Box
              key={`fixed-${rowIndex}`}
              sx={{
                position: "relative",
                height: "3rem", // 固定行高度
                display: "flex",
                alignItems: "center", // 垂直居中对齐
                mb: "1rem",
              }}>
              <Typography variant="body2" sx={{ fontSize: "var(--font-size-xs)", fontWeight: 600 }}>
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 可滚动的其他列容器 - 带阴影指示的滚动区域 */}
        <Box
          sx={{
            position: "relative", // 为阴影定位提供相对位置
            flex: 1, // 占据剩余空间
            overflow: "hidden", // 隐藏滚动条
          }}>
          {/* 左侧阴影指示器 */}
          {showLeftShadow && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: "1.5rem",
                width: "20px",
                background: `linear-gradient(to right, ${
                  theme.isDarkMode 
                    ? "rgba(0, 0, 0, 0.3), transparent" 
                    : "rgba(0, 0, 0, 0.1), transparent"
                })`,
                zIndex: 2,
                pointerEvents: "none", // 不阻塞鼠标事件
              }}
            />
          )}
          
          {/* 右侧阴影指示器 */}
          {showRightShadow && (
            <Box
              sx={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: "1.5rem",
                width: "20px",
                background: `linear-gradient(to left, ${
                  theme.isDarkMode 
                    ? "rgba(0, 0, 0, 0.3), transparent" 
                    : "rgba(0, 0, 0, 0.1), transparent"
                })`,
                zIndex: 2,
                pointerEvents: "none", // 不阻塞鼠标事件
              }}
            />
          )}
          
          {/* 滚动内容容器 */}
          <Box
            ref={scrollContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            sx={{
              display: "flex", // 水平排列各列
              overflowX: "auto", // 启用水平滚动
              overflowY: "hidden", // 禁用垂直滚动
              // 隐藏滚动条
              scrollbarWidth: "none", // Firefox
              "&::-webkit-scrollbar": {
                display: "none", // Chrome, Safari, Edge
              },
              // 移动端触摸滚动优化
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-x',
              // 确保内容有足够宽度触发滚动
              minWidth: "100%",
              // 鼠标拖拽样式
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none', // 防止拖拽时选中文本
              // 拖拽时的平滑过渡
              transition: isDragging ? 'none' : 'scroll-behavior 0.3s ease',
            }}>
            {/* 按列渲染 - 每列包含标题、功能特性和按钮 */}
            {planColumns.slice(1).map((column, columnIndex) => {
              // 获取当前列的 PlanCode
              const columnPlanCode = column.planCode;

              // 只有有 PlanCode 的列才进行降级检查
              const isDowngrade = columnPlanCode ? isPlanDowngrade(currentPlanCode, columnPlanCode) : false;
              return (
                <Box
                  key={column.planCode || `col-${columnIndex}`}
                  sx={{
                    width: { xs: "120px", sm: "160px" }, // 响应式固定宽度
                    minWidth: { xs: "120px", sm: "160px" }, // 确保最小宽度
                    flexShrink: 0, // 不允许收缩
                    flexGrow: 0, // 不允许增长
                  }}>
                  <Box
                    sx={{
                      backgroundColor: column.hasBackground ? "background.paperMedium" : "transparent",
                      borderRadius: column.hasBackground ? "1.25rem" : "0", // 圆角
                      pb: "1rem",
                      pt: "1rem",
                      px: { xs: "0.25rem", sm: "0.5rem" }, // 响应式水平内边距
                      height: "fit-content", // 高度自适应
                      width: "100%", // 占满父容器宽度
                    }}>
                    {/* 列标题 */}
                    <Box
                      sx={{
                        textAlign: "center",
                        mb: "1rem",
                        height: "3rem", // 与左侧固定列表头相同高度
                        display: "flex",
                        alignItems: "center", // 垂直居中对齐
                        justifyContent: "center", // 水平居中对齐
                      }}>
                      <Typography
                        variant="body2"
                        sx={{ 
                          fontSize: "var(--font-size-xs)", // 响应式字体大小
                          fontWeight: "600", 
                          color: "text.primary"
                        }}>
                        {column.title}
                        {column.subtitle && (
                          <>
                            <br />
                            {column.subtitle}
                          </>
                        )}
                      </Typography>
                    </Box>

                    {/* 列功能特性 */}
                    {column.features.map((feature, featureIndex) => (
                      <Box
                        key={`${columnIndex}-${featureIndex}`}
                        sx={{
                          textAlign: "center",
                          height: "3rem", // 与左侧固定列行相同高度
                          mb: "1rem",
                          display: "flex",
                          alignItems: "center", // 垂直居中对齐
                          justifyContent: "center", // 水平居中对齐
                        }}>
                        {renderFeatureValue(feature)}
                      </Box>
                    ))}
                  </Box>
                  {/* 列按钮 - 绝对定位到背景底边 */}
                  {columnPlanCode && column.buttonType && column.buttonType !== "none" ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: "-1rem" }}>
                      <Button
                        variant="contained"
                        color="tertiary"
                        size="small"
                        disabled={isDowngrade}
                        onClick={() => handlePlanClick(columnPlanCode)}
                        sx={{
                          borderRadius: "1.25rem",
                          fontSize: "0.75rem",
                          py: "0.3125rem",
                          px: "1rem",
                        }}>
                        {t("upgrade")}
                      </Button>
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* 底部说明文字 */}
      <Box sx={{ textAlign: "center", mt: "3.75rem" }}>
        <Typography variant="body2">{t("footer")}</Typography>
      </Box>
    </Box>
  );
}

// 导出接口类型供其他组件使用
export type { PlanColumn };
